import ActionCheckbox from '@/src/components/actions/action-checkbox/ActionCheckbox'
import { t } from 'i18next'
import { useMemo, useState } from 'react'
import { VpBadge, VpIcon, VpIconButton } from '@vtmn-play/react'
import { ActionPlanItem } from '@/infra/api/generated/model'
import { CustomActionInput } from '@/src/components/actions/custom-action-input/CustomActionInput'

type Props = {
  actions?: ActionPlanItem[]
  listIsDisabled?: boolean
  actionValueChange?: (actionId: ActionPlanItem, done: boolean) => void
  actionLabelChange?: (actionId: string, label: string) => Promise<void>
  actionDelete?: (actionId: string) => Promise<void>
}

const ActionList = ({
  actions,
  listIsDisabled,
  actionValueChange,
  actionLabelChange,
  actionDelete,
}: Props) => {
  const handleActionCheckboxChange = (action: ActionPlanItem, value: boolean) => {
    if (!listIsDisabled && actionValueChange) {
      actionValueChange(action, value)
    }
  }

  const sortedActions = useMemo(
    () =>
      actions?.slice().sort((a, b) => {
        if (a.done !== b.done) {
          return a.done ? 1 : -1
        }
        if (a.order !== b.order) {
          return a.order - b.order
        }
        return Number(a.isCustom) - Number(b.isCustom)
      }),
    [actions]
  )
  const actionsToDoCount = actions?.filter((action) => !action.done).length
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-xl">{t('pages.action-list.actions-to-do')}</span>
        <VpBadge className="h-5 w-5">{actionsToDoCount || 0}</VpBadge>
      </div>
      <div className="flex flex-col gap-4">
        {sortedActions?.map((action) => (
          <ActionItem
            key={action.actionPlanItemId}
            label={action.label}
            done={action.done}
            isCustom={action.isCustom}
            disabled={listIsDisabled}
            onChange={(value) => handleActionCheckboxChange(action, value)}
            onLabelChange={(label) => actionLabelChange?.(action.actionId, label)}
            onDelete={() => actionDelete?.(action.actionId)}
          />
        ))}
      </div>
    </div>
  )
}

interface ActionItemProps {
  label: string
  done: boolean
  isCustom?: boolean
  disabled?: boolean
  onChange: (value: boolean) => void
  onLabelChange: (label: string) => Promise<void> | undefined
  onDelete: () => Promise<void> | undefined
}

const ActionItem = ({
  label,
  done,
  isCustom,
  disabled,
  onChange,
  onLabelChange,
  onDelete,
}: ActionItemProps) => {
  const [isEditing, setIsEditing] = useState(false)

  const handleValidate = async (value: string) => {
    await onLabelChange(value)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    await onDelete()
    setIsEditing(false)
  }

  return (
    <>
      {isEditing ? (
        <CustomActionInput
          action={{ id: '', label, isCustom: true }}
          disabled={disabled}
          onValidate={handleValidate}
          onDelete={handleDelete}
        />
      ) : (
        <span className="flex justify-between gap-2">
          <ActionCheckbox
            label={label}
            done={done}
            isCustom={isCustom}
            disabled={disabled}
            onChange={onChange}
          />
          {isCustom && (
            <VpIconButton variantStyle="inverse" size="small" onClick={() => setIsEditing(true)}>
              <VpIcon name="edit" />
            </VpIconButton>
          )}
        </span>
      )}
    </>
  )
}

export default ActionList
